import { Link, useNavigate } from 'react-router-dom'
import { getItemLink, getItemId, getItemName, getItemIcon } from './utils'
import HighlightText from './HighlightText'
import noFavorite from '../../assets/images/noFavorite.png'
import favorite from '../../assets/images/yesFavorite.png'
import { useContentAccess } from '../../hooks/useApi'
import CountdownTimer from '../CountdownTimer/CountdownTimer'
import { ROUTES, WEDDING_COURSE_ID } from '@shared/config/routes'
import { CoverCard } from '@shared/ui/CoverCard'

interface InteractiveCategoryCardProps {
  item: any
  showingType: 'subcategories' | 'cards' | 'courses'
  hasSubscription?: boolean
  className?: string
  onCategoryClick?: (categoryId: number) => void
  onToggleFavorite?: (itemId: string) => void
  isFavorite?: boolean
  searchQuery?: string
  showLockBadge?: boolean
  showDemoBadge?: boolean
}

const InteractiveCategoryCard = ({
  item,
  showingType,
  hasSubscription,
  className = 'category-card',
  onCategoryClick,
  onToggleFavorite,
  isFavorite,
  searchQuery = '',
  showLockBadge,
  showDemoBadge,
}: InteractiveCategoryCardProps) => {
  const navigate = useNavigate()
  const { hasSubscription: userHasSubscription } = useContentAccess()

  const handleClick = (e: React.MouseEvent) => {
    if (showingType === 'subcategories' && onCategoryClick) {
      e.preventDefault()
      onCategoryClick(item.category_id)
      return
    }

    if (showingType === 'courses') {
      // Без подписки на подписочный курс — редирект на /subscribe
      if (isLockedBySubscription) {
        e.preventDefault()
        navigate('/subscribe')
        return
      }

      // С подпиской, но курс ещё не доступен по таймеру — блокируем
      if (isCourseNotYetAvailable) {
        e.preventDefault()
        return
      }
    }
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onToggleFavorite) {
      onToggleFavorite(getItemId(item, showingType).toString())
    }
  }

  const showFavoriteButton =
    hasSubscription && showingType === 'cards' && onToggleFavorite

  // Логика доступности курса
  const isCourse = showingType === 'courses'
  const isSubscriptionCourse =
    isCourse && item.base?.grant_type === 'subscription'
  const isLockedBySubscription = isSubscriptionCourse && !userHasSubscription
  const availableAfter = isCourse ? item.base?.available_after : null
  const isCourseNotYetAvailable = availableAfter
    ? new Date(availableAfter).getTime() > Date.now()
    : false

  // Данные для прогресса курса
  const courseProgress = isCourse ? item.progress : null
  const courseTotalLessons = isCourse
    ? item.base?.lesson_count || item.base?.total_lessons || 0
    : 0
  const courseViewedLessons =
    isCourse && item.access ? courseProgress?.views || 0 : 0
  const showProgress = isCourse && item.access && courseTotalLessons > 0

  const content = (
    <CoverCard
      isFluid
      isStatic
      image={getItemIcon(item, showingType)}
      title={
        <HighlightText
          text={getItemName(item, showingType)}
          searchQuery={searchQuery}
        />
      }
      isLocked={isLockedBySubscription || Boolean(showLockBadge)}
      tag={showDemoBadge ? 'Демо-доступ' : undefined}
      overlay={
        isCourseNotYetAvailable && availableAfter && !isLockedBySubscription ? (
          <CountdownTimer targetDate={availableAfter} />
        ) : undefined
      }
      action={
        showFavoriteButton ? (
          <button
            className={`favorite-button ${isFavorite ? 'favorite-active' : ''}`}
            onClick={handleFavoriteClick}
            title={
              isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'
            }
          >
            <img
              src={isFavorite ? favorite : noFavorite}
              alt={isFavorite ? 'favorite' : 'noFavorite'}
              className="favorite-icon"
            />
          </button>
        ) : undefined
      }
      footer={
        showProgress ? (
          <span className="course-progress-wrapper">
            <span className="course-progress-bar">
              <span
                className="course-progress-fill"
                style={{
                  width: `${courseTotalLessons > 0 ? (courseViewedLessons / courseTotalLessons) * 100 : 0}%`,
                }}
              />
            </span>
            <span className="course-progress-info">
              <span className="course-progress-label">Пройдено</span>
              <span className="course-progress-count">
                {courseViewedLessons}/{courseTotalLessons}
              </span>
            </span>
          </span>
        ) : undefined
      }
    />
  )

  // Свадебный курс с замком (без триалов и без доступа) — клик ведёт сразу на /wc,
  // минуя страницу курса с полноэкранным лендингом.
  const isWeddingLocked =
    isCourse &&
    showLockBadge &&
    (item.base?.course_id ?? item.course_id) === WEDDING_COURSE_ID
  const linkTarget = isWeddingLocked
    ? ROUTES.WC
    : getItemLink(item, showingType)

  return (
    <Link
      key={getItemId(item, showingType)}
      to={linkTarget}
      className={className}
      onClick={handleClick}
    >
      {content}
    </Link>
  )
}

export default InteractiveCategoryCard
